/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import {
  RefreshCw, AlertTriangle, Loader2, Beaker, ClipboardList,
  PackageCheck, ClipboardCheck, FileCheck, MessageSquare, ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const NEXT_ACTION_META = {
  delivery_issue:    { label: 'Delivery issue',          color: 'bg-rose-500/15 text-rose-200 border-rose-500/30' },
  verify_pending:    { label: 'Verify pending',          color: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
  snapshot_approve:  { label: 'Snapshot needs approval', color: 'bg-cyan-500/15 text-cyan-200 border-cyan-500/30' },
  plan_admin_action: { label: 'Plan needs admin action', color: 'bg-violet-500/15 text-violet-200 border-violet-500/30' },
  plan_explanation:  { label: 'Explanation pending',     color: 'bg-sky-500/15 text-sky-200 border-sky-500/30' },
  phase_preparation: { label: 'Phase preparation active', color: 'bg-white/5 text-white/60 border-white/10' },
  none:              { label: 'No action',                color: 'bg-white/5 text-white/40 border-white/10' },
};

const FILTER_OPTIONS = [
  { key: 'all',                label: 'All' },
  { key: 'delivery_issue',     label: 'Delivery issue' },
  { key: 'verify_pending',     label: 'Verify pending' },
  { key: 'snapshot_approve',   label: 'Snapshot needs approval' },
  { key: 'plan_admin_action',  label: 'Plan needs admin action' },
  { key: 'plan_explanation',   label: 'Explanation pending' },
  { key: 'phase_preparation',  label: 'In preparation' },
];

export default function FormulationDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(`${API}/formulation/dashboard/overview`);
      setData(r.data || { rows: [], total: 0, counts_by_next_action: {} });
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load formulation dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const counts = data?.counts_by_next_action || {};

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== 'all' && r.next_action !== filter) return false;
      if (!q) return true;
      return (
        (r.subscriber_name || '').toLowerCase().includes(q) ||
        (r.phase_name || '').toLowerCase().includes(q) ||
        (r.subscriber_id || '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, search]);

  if (!user) return null;

  return (
    <AdminLayout>
      <div
        data-testid="formulation-dashboard-page"
        className="px-6 py-6 max-w-7xl mx-auto"
      >
        <div className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <Beaker className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">Formulation Dashboard</h1>
            <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">
              F1–F5
            </Badge>
          </div>
          <p className="text-sm text-white/50">
            Active phase productions across {filteredRows.length} of {rows.length} subscribers.
            Prioritized by next operational action.
          </p>
        </div>

        {/* Summary strip */}
        <div data-testid="formulation-dashboard-summary" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
          <SummaryCard
            testId="summary-delivery-issue"
            label="Delivery issue"
            value={counts.delivery_issue || 0}
            icon={<AlertTriangle className="w-4 h-4 text-rose-300" />}
            active={filter === 'delivery_issue'}
            onClick={() => setFilter(filter === 'delivery_issue' ? 'all' : 'delivery_issue')}
          />
          <SummaryCard
            testId="summary-verify-pending"
            label="Verify pending"
            value={counts.verify_pending || 0}
            icon={<ClipboardCheck className="w-4 h-4 text-amber-300" />}
            active={filter === 'verify_pending'}
            onClick={() => setFilter(filter === 'verify_pending' ? 'all' : 'verify_pending')}
          />
          <SummaryCard
            testId="summary-snapshot-approve"
            label="Snapshot approve"
            value={counts.snapshot_approve || 0}
            icon={<FileCheck className="w-4 h-4 text-cyan-300" />}
            active={filter === 'snapshot_approve'}
            onClick={() => setFilter(filter === 'snapshot_approve' ? 'all' : 'snapshot_approve')}
          />
          <SummaryCard
            testId="summary-plan-admin-action"
            label="Plan action"
            value={counts.plan_admin_action || 0}
            icon={<PackageCheck className="w-4 h-4 text-violet-300" />}
            active={filter === 'plan_admin_action'}
            onClick={() => setFilter(filter === 'plan_admin_action' ? 'all' : 'plan_admin_action')}
          />
          <SummaryCard
            testId="summary-plan-explanation"
            label="Explanation"
            value={counts.plan_explanation || 0}
            icon={<MessageSquare className="w-4 h-4 text-sky-300" />}
            active={filter === 'plan_explanation'}
            onClick={() => setFilter(filter === 'plan_explanation' ? 'all' : 'plan_explanation')}
          />
          <SummaryCard
            testId="summary-phase-preparation"
            label="In preparation"
            value={counts.phase_preparation || 0}
            icon={<ClipboardList className="w-4 h-4 text-white/60" />}
            active={filter === 'phase_preparation'}
            onClick={() => setFilter(filter === 'phase_preparation' ? 'all' : 'phase_preparation')}
          />
          <SummaryCard
            testId="summary-total"
            label="Total"
            value={data?.total || 0}
            icon={<Beaker className="w-4 h-4 text-cyan-300" />}
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="rounded-lg border border-white/10 bg-slate-900/40 p-1 flex flex-wrap">
            {FILTER_OPTIONS.map((f) => (
              <FilterButton
                key={f.key}
                active={filter === f.key}
                onClick={() => setFilter(f.key)}
                testId={`filter-${f.key}`}
              >
                {f.label}
              </FilterButton>
            ))}
          </div>
          <input
            type="text"
            data-testid="dashboard-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subscriber or phase…"
            className="bg-slate-900/40 border border-white/10 rounded px-3 h-9 text-xs text-white/80 placeholder-white/30 w-64"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="border-white/15 text-white/70 h-9 text-xs"
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Link
            to="/admin/formulation/verification"
            data-testid="dashboard-open-verification-queue"
            className="ml-auto"
          >
            <Button
              variant="outline"
              size="sm"
              className="border-amber-500/30 text-amber-200 hover:bg-amber-500/10 h-9 text-xs"
            >
              <ClipboardCheck className="w-3.5 h-3.5 mr-1" />
              Open Verification Queue
            </Button>
          </Link>
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}

        {pageError && !loading && (
          <div
            data-testid="dashboard-page-error"
            className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
          >
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
            <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={load}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !pageError && rows.length === 0 && (
          <div
            data-testid="dashboard-empty-state"
            className="rounded-lg border border-white/10 bg-slate-900/40 p-10 text-center"
          >
            <Beaker className="w-8 h-8 mx-auto text-white/30 mb-3" />
            <div className="text-sm text-white/80 mb-1">No active phase productions.</div>
            <div className="text-xs text-white/50">
              Active phases appear once production records are created.
            </div>
          </div>
        )}

        {!loading && !pageError && rows.length > 0 && filteredRows.length === 0 && (
          <div
            data-testid="dashboard-filter-empty-state"
            className="rounded-lg border border-white/10 bg-slate-900/40 p-8 text-center"
          >
            <div className="text-sm text-white/70">No rows match the current filter.</div>
          </div>
        )}

        {!loading && !pageError && filteredRows.length > 0 && (
          <div
            data-testid="dashboard-rows"
            className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden"
          >
            <table className="w-full text-xs">
              <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Subscriber</th>
                  <th className="text-left px-3 py-2 font-medium">Phase</th>
                  <th className="text-left px-3 py-2 font-medium">Production</th>
                  <th className="text-left px-3 py-2 font-medium">Inventory</th>
                  <th className="text-left px-3 py-2 font-medium">Snapshot</th>
                  <th className="text-left px-3 py-2 font-medium">Plan</th>
                  <th className="text-left px-3 py-2 font-medium">Next action</th>
                  <th className="text-right px-3 py-2 font-medium">Quick links</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <DashboardRow key={`${r.subscriber_id}:${r.phase_id}`} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ testId, label, value, icon, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition-colors ${
        active
          ? 'bg-cyan-500/10 border-cyan-500/40'
          : 'bg-slate-900/40 border-white/10 hover:border-white/20'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-white/50">{label}</span>
        {icon}
      </div>
      <div className="text-xl font-semibold text-white">{value}</div>
    </button>
  );
}

function FilterButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded text-xs transition-colors ${
        active
          ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/30'
          : 'text-white/60 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function DashboardRow({ row }) {
  const meta = NEXT_ACTION_META[row.next_action] || NEXT_ACTION_META.none;
  const inv = row.inventory || {};
  const snap = row.snapshot_status;
  const plan = row.transition_plan;
  const rowTestId = `dashboard-row-${row.subscriber_id}-${row.phase_id}`;

  return (
    <tr
      data-testid={rowTestId}
      className="border-b border-white/5 hover:bg-white/[0.02] align-top"
    >
      <td className="px-3 py-2">
        <div className="text-white/90">{row.subscriber_name}</div>
        <div className="text-[10px] font-mono text-white/40">{row.subscriber_id}</div>
      </td>
      <td className="px-3 py-2">
        <div className="text-white/80">{row.phase_name || '—'}</div>
        <div className="text-[10px] font-mono text-white/40">{row.phase_id}</div>
      </td>
      <td className="px-3 py-2">
        <div className="text-white/70 font-mono text-[11px]">{row.production_status || '—'}</div>
        {row.delivery_status?.delivery_issue_open && (
          <Badge
            data-testid={`${rowTestId}-delivery-issue-badge`}
            className="mt-1 bg-rose-500/15 text-rose-200 border-rose-500/30 text-[9px]"
          >
            Issue open
          </Badge>
        )}
        {row.delivery_status?.subscriber_confirmed_delivery && (
          <Badge className="mt-1 ml-1 bg-emerald-500/15 text-emerald-200 border-emerald-500/30 text-[9px]">
            Delivered
          </Badge>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="text-white/70">
          {inv.total > 0 ? `${inv.total} rows` : '—'}
        </div>
        {inv.unverified_count > 0 && (
          <Badge
            data-testid={`${rowTestId}-unverified-badge`}
            className="mt-1 bg-amber-500/15 text-amber-200 border-amber-500/30 text-[9px]"
          >
            {inv.unverified_count} unverified
          </Badge>
        )}
        {inv.mismatch_count > 0 && (
          <Badge className="mt-1 ml-1 bg-rose-500/15 text-rose-200 border-rose-500/30 text-[9px]">
            {inv.mismatch_count} mismatch
          </Badge>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="text-white/70 font-mono text-[11px]">{snap || '—'}</div>
      </td>
      <td className="px-3 py-2">
        {plan ? (
          <>
            <div className="text-white/70 font-mono text-[11px]">{plan.status}</div>
            {!plan.has_counselor_explanation && (
              <Badge className="mt-1 bg-sky-500/15 text-sky-200 border-sky-500/30 text-[9px]">
                No explanation
              </Badge>
            )}
          </>
        ) : (
          <span className="text-white/40 italic text-[11px]">No plan yet</span>
        )}
      </td>
      <td className="px-3 py-2">
        <Badge
          data-testid={`${rowTestId}-next-action`}
          className={`text-[10px] ${meta.color}`}
        >
          {meta.label}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex flex-wrap gap-1 justify-end">
          <QuickLink to={row.links.production}     label="Production"  testId={`${rowTestId}-link-production`} />
          <QuickLink to={row.links.inventory}      label="Inventory"   testId={`${rowTestId}-link-inventory`} />
          <QuickLink to={row.links.feedback}       label="Feedback"    testId={`${rowTestId}-link-feedback`} />
          <QuickLink to={row.links.snapshot}       label="Snapshot"    testId={`${rowTestId}-link-snapshot`} />
          {row.links.transition_plan ? (
            <QuickLink
              to={row.links.transition_plan}
              label="Transition"
              testId={`${rowTestId}-link-transition`}
            />
          ) : (
            <span
              data-testid={`${rowTestId}-no-plan-chip`}
              className="px-2 py-0.5 rounded border border-white/10 text-[10px] text-white/40 italic"
            >
              No Plan Yet
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function QuickLink({ to, label, testId }) {
  return (
    <Link to={to} data-testid={testId}>
      <Button
        size="sm"
        variant="outline"
        className="border-white/15 text-white/70 hover:text-cyan-200 hover:border-cyan-500/30 h-6 px-2 text-[10px]"
      >
        {label}
        <ArrowRight className="w-2.5 h-2.5 ml-1" />
      </Button>
    </Link>
  );
}
